import React, { Suspense, useEffect } from 'react';
import { usePageTitle } from '@utils/hooks/usePageTitle';
import { Route, Switch, useParams, useHistory, matchPath, Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import MainContent from '@containers/MainContent';
import { actions as SectionActions } from '@reducers/sectionStudent';
import ChalkTalkErrorBoundary from '@components/ChalkTalkErrorBoundary';
import { useIsTeacher } from '@reducers/user/hooks';
import LoadingIndicator from '@components/LoadingIndicator';
import Skeleton from '@components/Atoms/Skeleton/Skeleton';
import AssessmentContainer from './Learnosity/AssessmentContainer';
import LeftSideBar from './Components/LeftSideBar';
import TestSessionScores from './Components/TestSessionScores';
import ReportsWrapper from './Components/Reports';
import PMExamStudentGuard from './Components/PMExamStudentGuard';
import { useGetLearnosityExamTestSessions, useNavigateToLETestlet, useNavigateToLESessionScores } from './hooks';
import TestContainer from './TestContainer';
import { useLearnosityExamAccess } from './ShowIfLearnosityExamFlagEnabled';
import LEActionRequired from './contexts/LEActionRequired';
import { getDefaultTestLanding, getFirstNotLockedTestlet } from './utils/getDefaultTestLanding';

const WrapWithMainContent = (Component) => {
  const WrappedComponent = (props) => (
    <MainContent>
      <Component {...props} />
    </MainContent>
  );

  WrappedComponent.displayName = `WrapWithMainContent(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};
const AssessmentContainerWrapper = () => (
  <ChalkTalkErrorBoundary>
    <Suspense fallback={<Skeleton variant="rectangular" width="100%" height={400} />}>
      <AssessmentContainer />
    </Suspense>
  </ChalkTalkErrorBoundary>
);

// Stable component references to prevent unnecessary remounts
const GuardedTestSessionScores = () => (
  <PMExamStudentGuard>
    <TestSessionScores />
  </PMExamStudentGuard>
);

const GuardedReportsWrapper = () => (
  <PMExamStudentGuard>
    <ReportsWrapper />
  </PMExamStudentGuard>
);

const useExamDomainNavigationFallback = () => {
  const history = useHistory();
  const { data: testSessions } = useGetLearnosityExamTestSessions();
  const navigateToLearnosityExam = useNavigateToLETestlet();
  const navigateToLearnosityExamScores = useNavigateToLESessionScores();
  useEffect(() => {
    const isTestSession = matchPath(history.location.pathname, {
      path: '/tests/course/:courseId/section/:sectionId/testsession/:testSessionId',
    });
    if (isTestSession) {
      return;
    }

    const landing = getDefaultTestLanding(testSessions);
    if (!landing) {
      return;
    }
    if (landing.kind === 'testlet') {
      navigateToLearnosityExam({
        testSessionId: landing.testSessionId.toString(),
        testletId: landing.testletId.toString(),
      });
      return;
    }
    navigateToLearnosityExamScores({
      testSessionId: landing.testSessionId.toString(),
    });
  }, [history, navigateToLearnosityExam, navigateToLearnosityExamScores, testSessions]);
};
const RedirectFromTestSession = () => {
  const { data: testSessions } = useGetLearnosityExamTestSessions();
  const { courseId, sectionId, testSessionId } = useParams();
  const firstNotLockedTestlet = getFirstNotLockedTestlet(testSessions?.filter((testSession) => testSession.id === +testSessionId));
  if (!firstNotLockedTestlet) {
    return <Redirect to={`/tests/course/${courseId}/section/${sectionId}/testsession/${testSessionId}/scores`} />;
  }
  return (
    <Redirect
      to={`/tests/course/${courseId}/section/${sectionId}/testsession/${firstNotLockedTestlet?.exam_session}/testlet/${firstNotLockedTestlet?.id}`}
    />
  );
};
const TestletRoutesController = () => {
  const { courseId, sectionId, testSessionId, testletId } = useParams();
  const { data: testSessions } = useGetLearnosityExamTestSessions();
  const isCurrentTestletValidTestlet = testSessions?.some(
    (testSession) => testSession.id === +testSessionId && testSession.testlets.some((testlet) => testlet.id === +testletId),
  );
  if (!isCurrentTestletValidTestlet) {
    return (
      <Redirect
        to={`/tests/course/${courseId}/section/${sectionId}/testsession/${testSessionId}/testlet/${testSessions?.[0]?.testlets?.[0]?.id}`}
      />
    );
  }
  return null;
};
const SessionRoutesController = () => {
  const { courseId, sectionId, testSessionId } = useParams();
  const { data: testSessions } = useGetLearnosityExamTestSessions();
  const isCurrentTestSessionValidTestSession = testSessions?.some((testSession) => testSession.id === +testSessionId);
  if (!isCurrentTestSessionValidTestSession) {
    const firstNotLockedTestlet = getFirstNotLockedTestlet(testSessions);
    if (!firstNotLockedTestlet) {
      return <Redirect to={`/tests/course/${courseId}/section/${sectionId}/testsession/${testSessions?.[0]?.id}/scores`} />;
    }
    return (
      <Redirect
        to={`/tests/course/${courseId}/section/${sectionId}/testsession/${firstNotLockedTestlet?.exam_session}/testlet/${firstNotLockedTestlet?.id}`}
      />
    );
  }
  return (
    <Switch>
      <Route
        path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId/testlet/:testletId"
        component={TestletRoutesController}
      />
    </Switch>
  );
};
const BaseRoutesController = () => {
  const { data: testSessions } = useGetLearnosityExamTestSessions();
  useExamDomainNavigationFallback();
  if (!testSessions || testSessions.length === 0) {
    return <Redirect to="/dashboard" />;
  }
  return <Route path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId" component={SessionRoutesController} />;
};
const Routes = () => {
  usePageTitle('Assessments');
  const isTeacher: boolean = useIsTeacher();
  const { courseId, sectionId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const isLearnosityExamEnabled = useLearnosityExamAccess();
  if (!isLearnosityExamEnabled && courseId && sectionId) {
    history.push(`/test/course/${courseId}/section/${sectionId}`);
  }

  React.useEffect(() => {
    if (!isTeacher) {
      return;
    }
    dispatch(SectionActions.sectionsStudentGet(sectionId));
  }, [dispatch, sectionId, isTeacher]);

  return (
    <LEActionRequired>
      <Route path="/tests/course/:courseId/section/:sectionId" component={BaseRoutesController} />
      <Switch>
        <Route exact path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId" component={LeftSideBar} />
        <Route exact path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId/scores" component={LeftSideBar} />
        <Route exact path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId/reports" component={LeftSideBar} />
        <Route
          exact
          path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId/testlet/:testletId"
          component={LeftSideBar}
        />
      </Switch>

      <Switch>
        {/* Entry page - lists available test sessions */}
        <Route exact path="/tests/course/:courseId/section/:sectionId" component={WrapWithMainContent(LoadingIndicator)} />
        <Route exact path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId" component={RedirectFromTestSession} />
        {/* Scores/Reports show weighted results to teachers for PM exams;
            students are redirected to the first testlet (raw scaled scores
            are not valid for category-scored exams) */}
        <Route
          exact
          path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId/scores"
          component={WrapWithMainContent(GuardedTestSessionScores)}
        />
        <Route
          exact
          path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId/reports"
          component={WrapWithMainContent(GuardedReportsWrapper)}
        />
        <Route
          exact
          path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId/testlet/:testletId"
          component={WrapWithMainContent(TestContainer)}
        />
        {/* Specific test session view */}
        <Route
          exact
          path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId"
          component={WrapWithMainContent(TestContainer)}
        />
        {/* Student taking the test view */}
        <Route
          exact
          path="/tests/course/:courseId/section/:sectionId/testsession/:testSessionId/testlet/:testletId/take"
          component={AssessmentContainerWrapper}
        />
      </Switch>
    </LEActionRequired>
  );
};

export default Routes;
